import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trophy, Users, CreditCard, Settings2, ExternalLink, Upload, X, Gamepad2, Loader2, Save, MapPin } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RequireUser from "@/components/public/RequireUser";
import { FORMAT_LABEL, fmtDateTime } from "@/components/tournament/constants";
import { STATUS_STYLE } from "./Tournaments";
import { toast } from "sonner";
import GoLivePanel from "@/components/community/GoLivePanel";
import MyGames from "@/components/games/MyGames";

const inpCls = "w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))] transition-colors";

function GamerProfileCard() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    let ignore = false;
    api.get("/members/me").then((r) => { if (!ignore) setP((cur) => cur || normalize(r.data)); }).catch(() => { if (!ignore) setP((cur) => cur || normalize({})); });
    return () => { ignore = true; };
  }, []);
  const normalize = (d) => ({ name: d.name || "", nickname: d.nickname || "", country: d.country || "", team: d.team || "",
    games: [...(d.games || []), "", "", ""].slice(0, 3), titles: d.titles || [], avatar: d.avatar || "", follower_count: d.follower_count || 0 });

  const setGame = (i, v) => setP({ ...p, games: p.games.map((g, idx) => (idx === i ? v : g)) });
  const addTitle = () => setP({ ...p, titles: [...p.titles, ""] });
  const setTitle = (i, v) => setP({ ...p, titles: p.titles.map((t, idx) => (idx === i ? v : t)) });
  const rmTitle = (i) => setP({ ...p, titles: p.titles.filter((_, idx) => idx !== i) });

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/members/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP((cur) => ({ ...cur, avatar: r.data.url }));
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed (JPG/PNG/WEBP, max 5MB)"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...p, games: p.games.filter((g) => g.trim()), titles: p.titles.filter((t) => t.trim()) };
      await api.put("/members/me", body);
      toast.success("Profile saved");
    } catch { toast.error("Could not save profile"); }
    finally { setSaving(false); }
  };

  if (!p) return <div className="card-surface rounded-2xl p-6 text-white/40 mb-8"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="card-surface rounded-2xl p-5 md:p-6 mb-8" data-testid="gamer-profile-card">
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Gamepad2 size={18} className="text-[hsl(var(--primary))]" /> Gamer Profile</h2>
        <span className="text-xs text-white/50"><b className="text-white">{p.follower_count}</b> followers</span>
      </div>

      <div className="flex items-center gap-4 mb-5">
        {p.avatar ? <img src={mediaUrl(p.avatar)} alt="" className="w-20 h-20 rounded-2xl object-cover border border-white/10" data-testid="gamer-avatar-preview" />
          : <span className="w-20 h-20 rounded-2xl bg-white/5 border border-white/10 grid place-items-center text-white/30"><Gamepad2 size={26} /></span>}
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="gamer-avatar-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="gamer-avatar-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-semibold hover:bg-white/15 disabled:opacity-60">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload photo
          </button>
          <p className="text-xs text-white/40 mt-1.5">Square image works best · max 5MB</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-xs text-white/50 mb-1 block">Display name</label><input className={inpCls} value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} placeholder="Your name" data-testid="gamer-name" /></div>
        <div><label className="text-xs text-white/50 mb-1 block">Game nickname</label><input className={inpCls} value={p.nickname} onChange={(e) => setP({ ...p, nickname: e.target.value })} placeholder="e.g. ShadowStrike" data-testid="gamer-nickname" /></div>
        <div><label className="text-xs text-white/50 mb-1 block flex items-center gap-1"><MapPin size={11} /> Country</label><input className={inpCls} value={p.country} onChange={(e) => setP({ ...p, country: e.target.value })} placeholder="e.g. Indonesia" data-testid="gamer-country" /></div>
        <div><label className="text-xs text-white/50 mb-1 block">Team (optional)</label><input className={inpCls} value={p.team} onChange={(e) => setP({ ...p, team: e.target.value })} placeholder="Team name" data-testid="gamer-team" /></div>
      </div>

      <div className="mt-4">
        <label className="text-xs text-white/50 mb-1 block">Games you play (max 3)</label>
        <div className="grid sm:grid-cols-3 gap-2">
          {p.games.map((g, i) => <input key={i} className={inpCls} value={g} onChange={(e) => setGame(i, e.target.value)} placeholder={`Game ${i + 1}`} data-testid={`gamer-game-${i}`} />)}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-white/50 flex items-center gap-1"><Trophy size={11} /> Championship titles</label>
          <button onClick={addTitle} data-testid="gamer-add-title" className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--primary))]"><Plus size={12} /> Add</button>
        </div>
        <div className="space-y-2">
          {p.titles.length === 0 && <p className="text-xs text-white/30">No titles yet. Add tournaments you've won.</p>}
          {p.titles.map((t, i) => (
            <div key={i} className="flex gap-2">
              <input className={inpCls} value={t} onChange={(e) => setTitle(i, e.target.value)} placeholder="e.g. Champion — IEM Katowice 2027" data-testid={`gamer-title-${i}`} />
              <button onClick={() => rmTitle(i)} data-testid={`gamer-title-remove-${i}`} className="p-2.5 rounded-lg border border-white/10 text-white/50 hover:text-white"><X size={15} /></button>
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="gamer-save"
        className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-bold disabled:opacity-60 hover:brightness-110">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save profile
      </button>
    </div>
  );
}

function TournamentRow({ t }) {
  const navigate = useNavigate();
  const st = STATUS_STYLE[t.status] || STATUS_STYLE.upcoming;
  const unpaid = t.payment_status === "pending";
  return (
    <div className="card-surface rounded-2xl p-4 flex flex-col sm:flex-row gap-4 sm:items-center hover-rise" data-testid={`my-tournament-${t.id}`}>
      <div className="w-full sm:w-36 h-24 rounded-xl overflow-hidden bg-white/5 shrink-0">{t.cover && <img src={mediaUrl(t.cover)} alt="" className="w-full h-full object-cover" />}</div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="eyebrow px-2 py-0.5 rounded text-white" style={{ background: st.color }}>{st.label}</span>
          {unpaid && <span className="eyebrow px-2 py-0.5 rounded bg-amber-500/20 text-amber-400" data-testid={`unpaid-badge-${t.id}`}>Awaiting payment</span>}
        </div>
        <h3 className="mt-2 text-lg font-bold text-white truncate">{t.name}</h3>
        <div className="text-xs text-white/50 flex flex-wrap gap-4 mt-1">
          <span>{t.game || "—"} · {FORMAT_LABEL[t.format]}</span><span className="flex items-center gap-1"><Users size={12} /> {t.participant_count} / {t.max_teams}</span><span>{fmtDateTime(t.start_date)}</span>
        </div>
      </div>
      <div className="flex gap-2">
        {t.published && <Link to={`/tournaments/${t.slug}`} className="p-2.5 rounded-full border border-white/15 text-white/70 hover:text-white" title="View public page"><ExternalLink size={15} /></Link>}
        <button onClick={() => navigate(`/profile/tournaments/${t.id}`)} data-testid={`manage-my-${t.id}`}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${unpaid ? "bg-white text-black" : "bg-white/10 text-white hover:bg-white/20"}`}>
          {unpaid ? <><CreditCard size={15} /> Pay & Manage</> : <><Settings2 size={15} /> Manage</>}
        </button>
      </div>
    </div>
  );
}

function ProfileInner() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/manage/tournaments").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  const live = (items || []).filter((t) => ["live", "registration", "upcoming"].includes(t.status)).length;
  return (
    <div className="mx-auto max-w-5xl px-4 md:px-6 py-10" data-testid="profile-page">
      <div className="flex flex-wrap items-center gap-5 mb-10">
        {user.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-full" /> : <span className="w-16 h-16 rounded-full bg-[hsl(var(--primary))] grid place-items-center text-2xl font-bold text-white">{user.name?.[0]}</span>}
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold text-white truncate" data-testid="profile-name">{user.name}</h1>
          <p className="text-sm text-white/50">{user.email}</p>
        </div>
        <Link to="/tournaments/create" data-testid="profile-create-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-white font-bold hover:brightness-110"><Plus size={16} /> Create Tournament</Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
        {[["Tournaments", items?.length ?? "–"], ["Active", live], ["Teams joined", (items || []).reduce((s, t) => s + t.participant_count, 0)]].map(([k, v]) => (
          <div key={k} className="card-surface rounded-xl p-4"><div className="text-2xl font-bold font-head text-white">{v}</div><div className="text-xs text-white/50">{k}</div></div>
        ))}
      </div>
      <GamerProfileCard />
      <MyGames />
      <GoLivePanel />
      <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Trophy size={18} /> My Tournaments</h2>
      {items === null ? <div className="text-white/40">Loading…</div> : items.length === 0 ? (
        <div className="card-surface rounded-2xl p-10 text-center text-white/50" data-testid="profile-empty">You haven't created a tournament yet.</div>
      ) : <div className="space-y-3">{items.map((t) => <TournamentRow key={t.id} t={t} />)}</div>}
    </div>
  );
}

export default function Profile() {
  return <RequireUser message="Sign in to see your profile and tournaments."><ProfileInner /></RequireUser>;
}
